import {
    CustomerOrderProfitabilityPlanLineKeyValues
} from "src/api/generated/erp/db/types/enums/customerOrderProfitabilityPlanLineKey.ts";
import {
    CustomerOrderProfitabilityLineKey,
    getCustomerOrderProfitabilityLineKeyLabel,
} from "src/api/generated/erp/sales/customerOrder/profitability/model/customerOrderProfitabilityLineKey.ts";
import i18n from "i18next";

export const getCustomerOrderProfitabilityDisplayLabel = (
    lineKey: CustomerOrderProfitabilityLineKey,
): string => {
    if (lineKey === "REVENUE") {
        return i18n.t("domestic_revenue");
    }

    return getCustomerOrderProfitabilityLineKeyLabel(lineKey);
};

export const isCustomerOrderProfitabilityLineKeyEditable = (
    lineKey: CustomerOrderProfitabilityLineKey,
): boolean => {
    switch (lineKey) {
        case "REVENUE":
        case "CUSTOMER_ORDER_LINE_COST_WAREHOUSE":
        case "CUSTOMER_ORDER_LINE_COST_PURCHASE_TRANSIT":
        case "CUSTOMER_ORDER_LINE_COST_PURCHASE_DIRECT":
        case "CUSTOMER_ORDER_LINE_COST_MANUFACTURE":
        case "CUSTOMER_ORDER_LINE_COST_NO_ACQUISITION":
        case "CUSTOMER_ORDER_LINE_COST_SPLIT_BOM":
        case "PROJECT_MATERIAL_COST_WAREHOUSE":
        case "PROJECT_MATERIAL_COST_PURCHASE_TRANSIT":
        case "PROJECT_MATERIAL_COST_PURCHASE_DIRECT":
        case "PROJECT_MATERIAL_COST_MANUFACTURE":
        case "PROJECT_MATERIAL_COST_NO_ACQUISITION":
        case "PROJECT_MATERIAL_COST_SPLIT_BOM":
        case "PROJECT_COST_EVENTS":
        case "CUSTOMER_ORDER_COST_EVENTS":
        case "RECLAMATION_COSTS":
            return true;
        case "CUSTOMER_ORDER_LINE_COSTS":
        case "PROJECT_MATERIAL_COSTS":
        case "OTHER_COSTS":
        case "TOTAL_COSTS":
        case "MARGIN":
        case "MARGIN_PERCENT":
            return false;
    }
};

export const toCustomerOrderProfitabilityPlanLineKey = (
    lineKey: CustomerOrderProfitabilityLineKey,
): (typeof CustomerOrderProfitabilityPlanLineKeyValues)[number] => {
    switch (lineKey) {
        case "REVENUE":
            return "REVENUE";
        case "CUSTOMER_ORDER_LINE_COST_WAREHOUSE":
            return "CUSTOMER_ORDER_LINE_COST_WAREHOUSE";
        case "CUSTOMER_ORDER_LINE_COST_PURCHASE_TRANSIT":
            return "CUSTOMER_ORDER_LINE_COST_PURCHASE_TRANSIT";
        case "CUSTOMER_ORDER_LINE_COST_PURCHASE_DIRECT":
            return "CUSTOMER_ORDER_LINE_COST_PURCHASE_DIRECT";
        case "CUSTOMER_ORDER_LINE_COST_MANUFACTURE":
            return "CUSTOMER_ORDER_LINE_COST_MANUFACTURE";
        case "CUSTOMER_ORDER_LINE_COST_NO_ACQUISITION":
            return "CUSTOMER_ORDER_LINE_COST_NO_ACQUISITION";
        case "CUSTOMER_ORDER_LINE_COST_SPLIT_BOM":
            return "CUSTOMER_ORDER_LINE_COST_SPLIT_BOM";
        case "PROJECT_MATERIAL_COST_WAREHOUSE":
            return "PROJECT_MATERIAL_COST_WAREHOUSE";
        case "PROJECT_MATERIAL_COST_PURCHASE_TRANSIT":
            return "PROJECT_MATERIAL_COST_PURCHASE_TRANSIT";
        case "PROJECT_MATERIAL_COST_PURCHASE_DIRECT":
            return "PROJECT_MATERIAL_COST_PURCHASE_DIRECT";
        case "PROJECT_MATERIAL_COST_MANUFACTURE":
            return "PROJECT_MATERIAL_COST_MANUFACTURE";
        case "PROJECT_MATERIAL_COST_NO_ACQUISITION":
            return "PROJECT_MATERIAL_COST_NO_ACQUISITION";
        case "PROJECT_MATERIAL_COST_SPLIT_BOM":
            return "PROJECT_MATERIAL_COST_SPLIT_BOM";
        case "PROJECT_COST_EVENTS":
            return "PROJECT_COST_EVENTS";
        case "CUSTOMER_ORDER_COST_EVENTS":
            return "CUSTOMER_ORDER_COST_EVENTS";
        case "RECLAMATION_COSTS":
            return "RECLAMATION_COSTS";
        default:
            throw new Error(`Profitability line key ${lineKey} cannot be saved as a plan line`);
    }
};

export const sortCustomerOrderProfitabilityRows = <TRow extends { lineKey: CustomerOrderProfitabilityLineKey }>(
    rows: Array<TRow>,
): Array<TRow> => {
    return [...rows].sort((left, right) => displayOrderIndexByLineKey[left.lineKey] - displayOrderIndexByLineKey[right.lineKey]);
};

const customerOrderProfitabilityDisplayOrder = [
    "REVENUE",
    "CUSTOMER_ORDER_LINE_COSTS",
    "CUSTOMER_ORDER_LINE_COST_WAREHOUSE",
    "CUSTOMER_ORDER_LINE_COST_PURCHASE_TRANSIT",
    "CUSTOMER_ORDER_LINE_COST_PURCHASE_DIRECT",
    "CUSTOMER_ORDER_LINE_COST_MANUFACTURE",
    "CUSTOMER_ORDER_LINE_COST_NO_ACQUISITION",
    "CUSTOMER_ORDER_LINE_COST_SPLIT_BOM",
    "PROJECT_MATERIAL_COSTS",
    "PROJECT_MATERIAL_COST_WAREHOUSE",
    "PROJECT_MATERIAL_COST_PURCHASE_TRANSIT",
    "PROJECT_MATERIAL_COST_PURCHASE_DIRECT",
    "PROJECT_MATERIAL_COST_MANUFACTURE",
    "PROJECT_MATERIAL_COST_NO_ACQUISITION",
    "PROJECT_MATERIAL_COST_SPLIT_BOM",
    "OTHER_COSTS",
    "PROJECT_COST_EVENTS",
    "CUSTOMER_ORDER_COST_EVENTS",
    "RECLAMATION_COSTS",
    "TOTAL_COSTS",
    "MARGIN",
    "MARGIN_PERCENT",
] as const satisfies Array<CustomerOrderProfitabilityLineKey>;

const displayOrderIndexByLineKey = Object.fromEntries(
    customerOrderProfitabilityDisplayOrder.map((lineKey, index) => [lineKey, index]),
) as Record<CustomerOrderProfitabilityLineKey, number>;
